import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Dictation CSAT Atoms
 *
 * State management for the dictation CSAT feedback popover.
 *
 * ## Trigger logic
 * After each successful dictation, the counter increments. Show probability follows
 * (counter / 30)^2, guaranteed at 30. Resets to 0 when shown.
 *
 * ## Dismiss behavior
 * On dismiss, the counter resets to -30 (COOLDOWN_AFTER_DISMISS). The next 30
 * dictations all produce 0% probability, then the exponential curve restarts.
 */

const CSAT_COUNTER_KEY = 'heidi:dictation-csat-counter';

const CSAT_POPOVER_STATE_KEY = 'heidi:dictation-csat-popover-state';

const CSAT_LOGS_FIRST_VISIT_KEY = 'heidi:dictation-csat-logs-first-visit-done';

const MAX_BEFORE_GUARANTEED = 30;

const COOLDOWN_AFTER_DISMISS = 30;

export const rollDictationCsatCadence = (current: number) => {
  const next = current + 1;
  const probability = next <= 0 ? 0 : (next / MAX_BEFORE_GUARANTEED) ** 2;
  const shouldShow = next >= MAX_BEFORE_GUARANTEED || Math.random() < probability;

  return { nextCounter: shouldShow ? 0 : next, shouldShow };
};

// ─────────────────────────────────────────────────────────────────────────────
// Persisted Atoms (synced to localStorage)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Number of successful dictations since the last CSAT popover was shown.
 * Resets to 0 each time the popover is triggered.
 */
export const dictationCsatCounterAtom = atomWithStorage<number>(
  CSAT_COUNTER_KEY,
  0,
  undefined,
  { getOnInit: true }
);

/** Persisted so a pending survey survives a reload. `viewed` dedupes the
 *  viewed-analytics event; reset on each (re)open. */
export const dictationCsatPopoverStateAtom = atomWithStorage<{
  isOpen: boolean;
  viewed: boolean;
}>(CSAT_POPOVER_STATE_KEY, { isOpen: false, viewed: false });

/** Desktop-only: first dictation-logs visit force-shows the CSAT, later visits
 *  roll normally. `getOnInit: true` so the stored value hydrates synchronously —
 *  the trigger reads it in a mount effect, else returning users look first-time. */
export const dictationLogsCsatFirstVisitDoneAtom = atomWithStorage<boolean>(
  CSAT_LOGS_FIRST_VISIT_KEY,
  false,
  undefined,
  { getOnInit: true }
);

// ─────────────────────────────────────────────────────────────────────────────
// Action Atoms
// ─────────────────────────────────────────────────────────────────────────────

/** Increments the counter and shows the popover on the probability roll. Fired
 *  per dictation on web; per dictation-logs visit on desktop. */
export const dictationCsatIncrementAtom = atom(null, (get, set) => {
  const { nextCounter, shouldShow } = rollDictationCsatCadence(
    get(dictationCsatCounterAtom)
  );
  set(dictationCsatCounterAtom, nextCounter);

  if (shouldShow) {
    set(dictationCsatPopoverStateAtom, { isOpen: true, viewed: false });
  }
});

/** Force-shows the popover, resetting the counter like a normal show — used for
 *  the desktop first-visit guarantee. */
export const dictationCsatForceShowAtom = atom(null, (_, set) => {
  set(dictationCsatCounterAtom, 0);
  set(dictationCsatPopoverStateAtom, { isOpen: true, viewed: false });
});

/**
 * Hides the CSAT popover and resets counter to -COOLDOWN_AFTER_DISMISS so
 * the next 30 dictations have 0% show probability before the curve restarts.
 */
export const dictationCsatDismissAtom = atom(null, (_, set) => {
  set(dictationCsatPopoverStateAtom, { isOpen: false, viewed: false });
  set(dictationCsatCounterAtom, -COOLDOWN_AFTER_DISMISS);
});
